'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { Slider } from 'radix-ui';
import type React from 'react';
import { useCallback, useRef } from 'react';
import styles from './RangeSlider.module.scss';

export type MultiRange = [number, number];
export type SingleRange = [number];
type Range = MultiRange | SingleRange;

interface RangeSliderHistogramIntervalProps {
  height: number; // Height in percentage
  inRange?: boolean;
}

export interface RangeSliderProps<T extends Range> {
  value?: T;
  defaultValue?: T;
  min?: number;
  max?: number;
  step?: number;
  minStepsBetweenThumbs?: number;
  hideRange?: boolean;
  disabled?: boolean;
  label: string;
  id: string;
  minLabel?: string;
  maxLabel?: string;
  testId?: string;
  histogram?: RangeSliderHistogramIntervalProps[];
  onChange?: (value: T) => void;
  onCommit?: (value: T) => void;
}

// NEED TO ADD ALL THES REFS, COMMIT, HANDLEPOINTERDOWN, HANDLEPOINTERUP, HANDLEVALUECOMMIT
// BECAUSE RADIX DO NOT FIRE ONCOMMIT WHEN MAX IS DROPPED ON MIN, EMBARRASSING AF!!!!

export const RangeSlider = <T extends Range = MultiRange>({
  value,
  defaultValue,
  min = 0,
  max = 100,
  step = 1,
  minStepsBetweenThumbs = 0,
  hideRange,
  label,
  disabled,
  id,
  minLabel,
  maxLabel,
  testId = id,
  histogram,
  onChange,
  onCommit,
}: RangeSliderProps<T>) => {
  const t = useTranslations('components.rangeSlider');
  const isDragging = useRef(false);
  const hasCommitted = useRef(false);

  const commit = useCallback(
    (v: T) => {
      if (isDragging.current && !hasCommitted.current) {
        hasCommitted.current = true;
        onCommit?.(v);
      }
      isDragging.current = false;
    },
    [onCommit],
  );

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    isDragging.current = true;
    hasCommitted.current = false;
  }, []);

  const handleValueCommit = useCallback(
    (v: T) => {
      commit(v);
    },
    [commit],
  );

  const handlePointerUp = useCallback(() => {
    if (value) commit(value);
  }, [value, commit]);

  // determine how many thumbs to render
  const thumbsCount = (value ?? defaultValue ?? ([min, max] as T)).length;

  return (
    <div className={styles.rangeSlider}>
      {histogram && (
        <div className={styles.historgram} role="img" aria-label={t('histogramDataDistribution')}>
          {histogram.map((interval, index) => (
            <span
              key={`${id}-interval-${index}`}
              className={clsx(styles.interval, {
                [styles.intervalInRange]: interval.inRange,
              })}
              style={
                {
                  '--inverval-height': interval.height,
                } as React.CSSProperties
              }
              role="presentation"
            />
          ))}
        </div>
      )}
      <div className={styles.sliderWrapper}>
        {minLabel && (
          <span aria-hidden="true" className={styles.edgeLabel}>
            {minLabel}
          </span>
        )}
        <Slider.Root
          className={styles.root}
          defaultValue={defaultValue as Range}
          value={value as Range}
          min={min}
          max={max}
          step={step}
          minStepsBetweenThumbs={minStepsBetweenThumbs}
          name={label}
          disabled={disabled}
          data-testid={testId}
          onPointerDown={handlePointerDown}
          onValueChange={onChange}
          onValueCommit={handleValueCommit}
          onPointerUp={handlePointerUp}
        >
          <Slider.Track className={styles.track}>
            <Slider.Range
              className={clsx(styles.range, {
                [styles.rangeTransparent]: hideRange,
              })}
            />
          </Slider.Track>
          {Array.from({ length: thumbsCount }).map((_, i) => (
            <Slider.Thumb
              key={`${id}-thumb-${i}`}
              className={styles.thumb}
              aria-label={
                thumbsCount === 1 ? label : i === 0 ? t('thumbMinLabel') : t('thumbMaxLabel')
              }
            />
          ))}
        </Slider.Root>
        {maxLabel && (
          <span aria-hidden="true" className={styles.edgeLabel}>
            {maxLabel}
          </span>
        )}
      </div>
    </div>
  );
};
